import { useEffect, useMemo, useRef, useState } from "react";

import {
  avisoConflitoResidual,
  avisoFalhaDeSistema,
  cartoesDaAlteracao,
  categoriaDaAlteracao,
  CATEGORIA_LABEL,
  chaveAlteracao,
  conflitosRestantes,
  detalheConflitosResiduais,
  detalheSimulacaoAplicada,
  isAplicada,
  JUSTIFICATIVA_GERAL_MAX,
  mensagemAlteracaoRemovida,
  mensagemSimulacaoAplicadaComConflitos,
  MS,
  MS003_MOBILE,
  NAO_INFORMADO,
  pesoDaAlteracao,
  resumirCenario,
  ROTULO_ESTADO_APLICACAO,
  ROTULO_PROGRESSO_APLICACAO,
  rotuloAplicando,
  rotuloTentarNovamente,
  subtituloSimulacaoAplicada,
  TEXTO_APLICACAO_ATIVA_SUBTITULO,
  TEXTO_APLICACAO_PARCIAL_SUBTITULO,
  TEXTO_APLICADA,
  TEXTO_APLICADA_SUCESSO,
  TEXTO_APLICANDO_AVISO,
  TEXTO_JUSTIFICATIVA_OBRIGATORIA,
  TEXTO_TENTAR_NOVAMENTE_AVISO,
  TITULO_APLICACAO_ATIVA,
  TITULO_PAINEL_APLICAR,
  TITULO_SIMULACAO_APLICADA,
  tituloAplicacaoParcial,
  TOAST_ALTERACOES_REMOVIDAS,
  TOAST_ALTERACOES_REMOVIDAS_DETALHE,
  TOAST_SIMULACAO_APLICADA,
  TOAST_SIMULACAO_NAO_APLICADA,
  TOAST_SIMULACAO_NAO_APLICADA_DETALHE,
} from "@/modules/recursos/simulacao";
import type {
  CartaoAlteracao,
  DivergenciaAplicacao,
  ItemAplicacao,
} from "@/modules/recursos/simulacao";
import { useMediaQuery } from "@/hooks/use-media-query";

import { CARGA_TRABALHO_MOBILE_QUERY } from "@/shared/components/gantt-carga/mobile";
import {
  APLICANDO_TITULO,
  APLICAR_TITULO,
  CATEGORIAS,
  ESTADO_TAG,
  ROTULO_AJUSTAR,
  ROTULO_CONCLUIR,
  ROTULO_RETOMAR,
  ROTULO_TENTAR_NOVAMENTE,
  ROTULO_VOLTAR,
} from "./aplicar-simulacao-dialog.constants";
import { useAplicacaoSimulacao } from "./hooks/use-aplicacao-simulacao";
import type {
  AcaoAplicar,
  AplicarSimulacaoDialogProps,
  AplicarSimulacaoDialogViewModel,
  Aviso,
  CartaoProgresso,
  GrupoRevisao,
  ItemProgresso,
  ItemResumo,
} from "./types-aplicar-simulacao-dialog";

/** Referência estável quando não há divergências da revalidação. */
const SEM_DIVERGENCIAS: readonly DivergenciaAplicacao[] = [];

/** Contagem do Resumo: zero aparece como traço, como no protótipo. */
const contagem = (total: number) => (total === 0 ? NAO_INFORMADO : total);

function estadoDoCartao(item: ItemAplicacao) {
  return {
    estado: item.estado,
    rotulo: ROTULO_ESTADO_APLICACAO[item.estado],
    ...ESTADO_TAG[item.estado],
  };
}

/**
 * "Aplicar Simulação": cartões por tipo à esquerda, painel Resumo à direita com a
 * justificativa geral obrigatória e, ao confirmar, um POST /aplicacao. Concluir só se
 * o conjunto entrou. Falha (rascunho ATIVA) oferece Ajustar.
 */
export function useAplicarSimulacaoDialog({
  cenario,
  simulacaoId,
  versao,
  onAjustar,
  onConcluir,
  onVoltar,
  onDesfazer,
  onNotify,
  divergencias = SEM_DIVERGENCIAS,
}: AplicarSimulacaoDialogProps): AplicarSimulacaoDialogViewModel {
  const aplicacao = useAplicacaoSimulacao(cenario, simulacaoId, versao, divergencias);
  const resumo = useMemo(() => resumirCenario(cenario), [cenario]);
  const residuais = useMemo(() => conflitosRestantes(cenario), [cenario]);
  const [justificativa, setJustificativa] = useState("");
  const [tocada, setTocada] = useState(false);
  const mobile = useMediaQuery(CARGA_TRABALHO_MOBILE_QUERY);

  const { executando, itens } = aplicacao;
  const revisao = !aplicacao.iniciada;
  const emAndamento = !revisao && executando;
  // Revisão e resultado contam nas unidades da tela (substituição vale 2, como na faixa), não por item do POST.
  const unidades = (lista: typeof itens) =>
    lista.reduce((soma, item) => soma + pesoDaAlteracao(item.alteracao), 0);
  const total = revisao ? resumo.alteracoes : unidades(itens);
  const aplicadas = unidades(itens.filter(isAplicada));
  const naoAplicadas = unidades(itens.filter((item) => item.estado === "nao-aplicada"));
  const naoAplicadasDeSistema = unidades(
    itens.filter((item) => item.estado === "nao-aplicada" && item.motivo === "sistema"),
  );
  const tudoAplicado = !revisao && !executando && aplicacao.todasAplicadas;
  const falhaAtiva = !revisao && !executando && aplicacao.statusCabecalho === "ATIVA";
  const podeConcluir =
    !executando && !aplicacao.haPendencias && aplicacao.statusCabecalho !== "ATIVA";

  const erroJustificativa = justificativa.trim() === "" ? TEXTO_JUSTIFICATIVA_OBRIGATORIA : null;

  function confirmar() {
    setTocada(true);
    if (erroJustificativa) return;
    void aplicacao.aplicar(justificativa.trim());
  }

  // Ao fim do POST: tudo gravado avisa o sucesso (com os conflitos que restam, D-040);
  // nada gravado (ATIVA / rollback) avisa a falha. APLICADA_PARCIAL legado: só os estados.
  const rodando = useRef(false);
  useEffect(() => {
    if (executando) {
      rodando.current = true;
      return;
    }
    if (!rodando.current) return;
    rodando.current = false;
    if (itens.every(isAplicada)) {
      const conflitos = residuais.reduce((soma, item) => soma + item.total, 0);
      onNotify(
        conflitos > 0
          ? {
              severity: "warn",
              summary: mensagemSimulacaoAplicadaComConflitos(conflitos),
              detail: detalheConflitosResiduais(residuais.map((item) => item.recurso)),
            }
          : {
              severity: "success",
              summary: TOAST_SIMULACAO_APLICADA,
              detail: detalheSimulacaoAplicada(aplicadas),
            },
      );
    } else if (!itens.some(isAplicada)) {
      onNotify({
        severity: "error",
        summary: TOAST_SIMULACAO_NAO_APLICADA,
        detail: TOAST_SIMULACAO_NAO_APLICADA_DETALHE,
      });
    }
  }, [executando, itens, aplicadas, residuais, onNotify]);

  function desfazer({ chave, recurso }: CartaoAlteracao) {
    onDesfazer(chave);
    // Sem alterações restantes não há o que aplicar: volta para a simulação.
    if (cenario.alteracoes.length <= 1) {
      onNotify({
        severity: "warn",
        summary: TOAST_ALTERACOES_REMOVIDAS,
        detail: TOAST_ALTERACOES_REMOVIDAS_DETALHE,
      });
      onVoltar();
      return;
    }
    onNotify({ severity: "success", summary: mensagemAlteracaoRemovida(recurso) });
  }

  // O aviso da revalidação vai uma vez por alteração: no primeiro cartão dela (não no Removido).
  const comAviso = new Set<string>();
  function avisoDoCartao(cartao: CartaoAlteracao): string | undefined {
    if (cartao.removido || comAviso.has(cartao.chave)) return undefined;
    const divergencia = divergencias.find((item) => item.chave === cartao.chave);
    if (divergencia) comAviso.add(cartao.chave);
    return divergencia?.mensagem;
  }

  const grupos: GrupoRevisao[] = revisao
    ? CATEGORIAS.flatMap((categoria) => {
        const alteracoes = cenario.alteracoes
          .filter((alteracao) => categoriaDaAlteracao(alteracao) === categoria)
          .map((alteracao) => ({
            chave: chaveAlteracao(alteracao),
            cartoes: cartoesDaAlteracao(alteracao).map((cartao) => ({
              cartao,
              aviso: avisoDoCartao(cartao),
              desfazer: cartao.removido ? null : () => desfazer(cartao),
            })),
          }));
        return alteracoes.length > 0
          ? [
              {
                categoria,
                rotulo: CATEGORIA_LABEL[categoria],
                testId: `aplicar-grupo-${categoria}`,
                alteracoes,
              },
            ]
          : [];
      })
    : [];

  const progresso: ItemProgresso[] = revisao
    ? []
    : itens.map((item) => ({
        chave: item.chave,
        cartoes: cartoesDaAlteracao(item.alteracao).map((cartao, indice): CartaoProgresso => ({
          cartao,
          falha: indice === 0 && item.estado === "nao-aplicada",
          mensagem: indice === 0 && item.mensagem !== TEXTO_APLICADA ? item.mensagem : null,
          estado: indice === 0 ? estadoDoCartao(item) : null,
        })),
      }));

  const titulo = revisao
    ? APLICAR_TITULO
    : emAndamento
      ? APLICANDO_TITULO
      : tudoAplicado
        ? TITULO_SIMULACAO_APLICADA
        : falhaAtiva
          ? TITULO_APLICACAO_ATIVA
          : tituloAplicacaoParcial(aplicadas, total);
  const subtitulo = revisao
    ? MS.MS002
    : emAndamento
      ? MS.MS008
      : tudoAplicado
        ? subtituloSimulacaoAplicada(total)
        : aplicacao.haFalhaDeSistema
          ? MS.MS009
          : falhaAtiva
            ? TEXTO_APLICACAO_ATIVA_SUBTITULO
            : TEXTO_APLICACAO_PARCIAL_SUBTITULO;
  const tituloPainel = revisao
    ? TITULO_PAINEL_APLICAR.revisao
    : emAndamento
      ? TITULO_PAINEL_APLICAR.andamento
      : TITULO_PAINEL_APLICAR.resultado;

  const itensResumo: ItemResumo[] = [
    ...(revisao
      ? [
          {
            tipo: "linha" as const,
            chave: "alteracoes",
            rotulo: "Alterações",
            valor: resumo.alteracoes,
            testId: "aplicar-resumo-alteracoes",
          },
        ]
      : [
          {
            tipo: "linha" as const,
            chave: "aplicadas",
            rotulo: "Aplicadas",
            valor: `${aplicadas} de ${total}`,
            testId: "aplicar-progresso-contagem",
          },
          ...(emAndamento
            ? [
                {
                  tipo: "barra" as const,
                  chave: "barra",
                  valor: total === 0 ? 0 : Math.round((aplicadas / total) * 100),
                  rotulo: ROTULO_PROGRESSO_APLICACAO,
                },
              ]
            : []),
          ...(naoAplicadas > 0
            ? [
                {
                  tipo: "linha" as const,
                  chave: "nao-aplicadas",
                  rotulo: "Não aplicadas",
                  valor: naoAplicadas,
                  testId: "aplicar-progresso-nao-aplicadas",
                },
              ]
            : []),
        ]),
    {
      tipo: "obras",
      chave: "obras",
      rotulo: "Obras afetadas",
      obras: resumo.obras,
      testId: "aplicar-resumo-obras",
    },
    {
      tipo: "linha",
      chave: "profissionais",
      rotulo: "Profissionais",
      valor: contagem(resumo.profissionais),
      testId: "aplicar-resumo-profissionais",
    },
    {
      tipo: "linha",
      chave: "fornecedores",
      rotulo: "Fornecedores",
      valor: contagem(resumo.fornecedores),
      testId: "aplicar-resumo-fornecedores",
    },
    {
      tipo: "linha",
      chave: "locais",
      rotulo: "Locais",
      valor: contagem(resumo.locais),
      testId: "aplicar-resumo-locais",
    },
  ];

  const avisosConflito: Aviso[] = residuais.map(({ recurso, total: quantos }) => ({
    chave: `conflito-${recurso}`,
    tom: "warn",
    texto: avisoConflitoResidual(recurso, quantos),
    testId: "aplicar-aviso-conflito",
  }));
  const avisos: Aviso[] = revisao
    ? [
        ...avisosConflito,
        { chave: "gravacao", tom: "info", texto: mobile ? MS003_MOBILE : MS.MS003 },
      ]
    : emAndamento
      ? [{ chave: "andamento", tom: "info", texto: TEXTO_APLICANDO_AVISO }]
      : [
          ...avisosConflito,
          ...(tudoAplicado
            ? [
                {
                  chave: "sucesso",
                  tom: "success" as const,
                  texto: TEXTO_APLICADA_SUCESSO,
                  testId: "aplicar-aviso-sucesso",
                },
              ]
            : []),
          ...(aplicacao.haFalhaDeSistema
            ? [
                {
                  chave: "falha",
                  tom: "error" as const,
                  texto: avisoFalhaDeSistema(naoAplicadasDeSistema),
                },
                { chave: "reenvio", tom: "info" as const, texto: TEXTO_TENTAR_NOVAMENTE_AVISO },
              ]
            : []),
        ];

  const rotuloConfirmar = `Confirmar e aplicar (${total})`;
  const voltar: AcaoAplicar = {
    chave: "voltar",
    rotulo: ROTULO_VOLTAR,
    ariaLabel: ROTULO_VOLTAR,
    variante: "texto",
    testId: "aplicar-voltar",
    onClick: onVoltar,
  };
  const acoes: AcaoAplicar[] = revisao
    ? [
        {
          chave: "confirmar",
          rotulo: rotuloConfirmar,
          ariaLabel: rotuloConfirmar,
          icone: "pi pi-check",
          variante: "primaria",
          testId: "aplicar-confirmar",
          onClick: confirmar,
        },
        voltar,
      ]
    : executando
      ? [
          {
            chave: "aplicando",
            rotulo: rotuloAplicando(aplicadas, total),
            icone: "pi pi-sync",
            variante: "primaria",
            desabilitada: true,
            testId: "aplicar-confirmar",
          },
        ]
      : [
          // Concluir só com APLICADA / sem pendência. ATIVA (rollback) oferece Ajustar.
          ...(podeConcluir
            ? [
                {
                  chave: "concluir",
                  rotulo: ROTULO_CONCLUIR,
                  icone: "pi pi-check",
                  variante: "primaria" as const,
                  testId: "aplicar-concluir",
                  onClick: onConcluir,
                },
              ]
            : []),
          ...(aplicacao.haFalhaDeSistema
            ? [
                {
                  chave: "tentar-novamente",
                  rotulo: rotuloTentarNovamente(naoAplicadasDeSistema),
                  ariaLabel: ROTULO_TENTAR_NOVAMENTE,
                  icone: "pi pi-refresh",
                  variante: "primaria" as const,
                  testId: "aplicar-tentar-novamente",
                  onClick: () => void aplicacao.tentarNovamente(),
                },
              ]
            : []),
          ...(aplicacao.haSemConexao
            ? [
                {
                  chave: "retomar",
                  rotulo: ROTULO_RETOMAR,
                  variante: "primaria" as const,
                  testId: "aplicar-retomar",
                  onClick: () => void aplicacao.retomar(),
                },
              ]
            : []),
          ...(aplicacao.haAjustaveis
            ? [
                {
                  chave: "ajustar",
                  rotulo: ROTULO_AJUSTAR,
                  variante: "texto" as const,
                  testId: "aplicar-ajustar",
                  onClick: () => onAjustar(aplicacao.restantes()),
                },
              ]
            : []),
        ];

  const fechavel = revisao || podeConcluir;

  return {
    titulo,
    subtitulo,
    tituloPainel,
    fechavel,
    fechar: () => {
      if (!fechavel) return;
      if (revisao) onVoltar();
      else onConcluir();
    },
    mobile,
    total,
    revisao,
    grupos,
    itens: progresso,
    resumo: itensResumo,
    justificativa: {
      value: justificativa,
      error: revisao && tocada ? erroJustificativa : null,
      disabled: !revisao,
      hideRequiredHint: !revisao,
      maxLength: JUSTIFICATIVA_GERAL_MAX,
      digitar: setJustificativa,
    },
    avisos,
    acoes,
  };
}
